import { AsyncPipe } from '@angular/common';
import { Component, inject, Input, Output } from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { EditorModule } from '@tinymce/tinymce-angular';
import { AlertaService } from '../alerta/alerta.service';
import { iEntrada, iCatalogos, iAcuerdo } from '../entrada/entrada.interface';
import { EntradaService } from '../entrada/entrada.service';
import { LoadingService } from '../loading/loading.service';
import { UsuarioService } from '../usuario/usuario.service';
import { DespachoService } from './despacho.service';
import { catchError, finalize, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { AppState } from '../app.state';
import { selectCatalogos } from '../states/catalogos/selector';
import { selectEntrada, selectEntradaEmpresa } from '../states/entrada/selector';
import { selectAcuerdo } from '../states/acuerdo/selector';
import { cargaAcuerdo, cargaAcuerdoCampos } from '../states/acuerdo/actions';
import { ActivatedRoute } from '@angular/router';
import { selectUsuario } from '../states/usuario/selector';
import { iUsuario } from '../usuario/usuario.interface';
import { LoggerService } from '../logger/logger.service';

@Component({
  selector: 'app-despacho',
  standalone: true,
  imports: [MatToolbarModule, MatButtonModule, MatIconModule, MatTabsModule, ReactiveFormsModule, MatInputModule, 
    MatFormFieldModule, FormsModule, MatRadioModule, MatDatepickerModule, MatSelectModule, MatAutocompleteModule,
    AsyncPipe, MatChipsModule, EditorModule],
  templateUrl: './despacho.component.html',
  styleUrl: './despacho.component.css'
})
export class DespachoComponent {
  private active = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private entradaService = inject(EntradaService);
  private usuarioService = inject(UsuarioService);
  private alertaService = inject(AlertaService);
  private despachoService = inject(DespachoService);
  private formBuilder:FormBuilder = inject(FormBuilder);
  private logSrvc = inject(LoggerService);
  private idEntrada = this.active.snapshot.params['id'];
  private idTema = this.active.snapshot.params['tema'];
  private subscriptions = new Subscription();
  
  private readonly store: Store<AppState> = inject(Store);
  private readonly stateCatalogos: any = this.store.pipe(select(selectCatalogos));
  private readonly stateEntrada: any = this.store.pipe(select(selectEntrada));
  private readonly stateAcuerdo: any = this.store.pipe(select(selectAcuerdo));
  private readonly stateUsuario: any = this.store.pipe(select(selectUsuario));

  @Input() listenToFather!: Observable<boolean>;
  @Output() sendToFather: Subject<any> = new Subject();

  public oEntrada: iEntrada;
  public oAcuerdo: iAcuerdo;
  public oUsuario: iUsuario;
  public formDespacho!: FormGroup;
  public formTema!: FormGroup;
  public catTipoEnvio: any = [];
  public catTemas: any = [];
  public nuevoTema: boolean = false;
  public temasEnMemoria: any = [];
  public deshabilitado: boolean = false;

  constructor() {
    this.loadingService.show();

    this.oEntrada = structuredClone(this.stateEntrada.state().entrada);
    this.oAcuerdo = structuredClone(this.stateAcuerdo.state().acuerdo);
    this.oUsuario = structuredClone(this.stateUsuario.state().usuario);

    this.formDespacho = this.formBuilder.group({
      tipo_despacho: [this.oAcuerdo.id_tipo_despacho, [Validators.required]],
      tipo_tema: [this.oAcuerdo.id_tema, [Validators.required]],
      descripcion_tema: [this.oAcuerdo.descripcionTema]
    });
    
    this.formTema = this.formBuilder.group({
      descripcion_tema: ["",[Validators.required,Validators.minLength(1), Validators.maxLength(1000)]],
    });

    ///////////////////////////// ONCHANGE BUSINESS RULES //////////////////////////////
    this.formDespacho.get('tipo_despacho')?.valueChanges.subscribe(value => {
      this.businessRules('tipo_despacho', value);
    });

    this.formDespacho.get('tipo_tema')?.valueChanges.subscribe(value => {
      this.businessRules('tipo_tema', value);
    });
    ////////////////////////////////////////////////////////////////////////////////////
    this.loadingService.hide();
  }

  async ngOnInit() {
    this.loadingService.show();
    
    this.subscriptions.add(
      this.despachoService.getCatalogo(this.despachoService.cats.TIPO_DESPACHO).subscribe((result:any) => {
        this.catTipoEnvio = result;
      })
    );

    this.subscriptions.add(
      this.despachoService.consultarTemas(this.oAcuerdo.id_tipo_despacho, this.oAcuerdo.id_empresa, this.oAcuerdo.nombre_empresa).pipe(
        catchError(error =>  { throw new Error('SIN TEMAS') })
      ).subscribe((result:any) => {
        this.catTemas = result.temas;
      })
    );

    this.subscriptions.add(
      this.store.select(selectEntrada).subscribe((oEntrada: iEntrada) => {
        this.oEntrada = structuredClone(oEntrada);
      })
    );
    this.subscriptions.add(
      this.store.select(selectAcuerdo).subscribe((oAcuerdo: iAcuerdo) => {
        this.oAcuerdo = structuredClone(oAcuerdo);
      })
    );
    this.subscriptions.add(
      this.store.select(selectEntradaEmpresa).subscribe(oEmpresa => {
        if(this.oEntrada.empresa !== this.oAcuerdo.id_empresa) {
          this.formDespacho.get('tipo_tema')?.setValue('');
          this.fnConsultarTemas();
        }
      })
    );

    this.subscriptions.add(
      this.listenToFather.subscribe((oData: any) => {
        if(oData.accion == 'disabled' && this.oAcuerdo.id_edo_tema != 3) {
          this.deshabilitado = oData.deshabilitado;
          if(oData.deshabilitado) {
            this.formDespacho.disable({emitEvent: false});
          } else {
            this.formDespacho.enable({emitEvent: false});
          }
          this.validacionFormulario();
        }
        if(oData.accion == 'notificacion' && oData.tipo == 'despacho') {
          this.enviaNotificacion();
        }
      })
    );

    this.formDespacho.statusChanges.subscribe((status:any) => {
      this.validacionFormulario()
    });

    if(this.idEntrada != 'add') {
      this.formDespacho.disable({emitEvent: false});
      this.deshabilitado = true;
    }

    this.validacionFormulario();
    this.sendToFather.next({accion: 'ACUERDO CARGADO'});
    this.loadingService.hide();
  }

  validacionFormulario() {
    Promise.resolve().then( () => {
      const status = this.formDespacho.status;
      this.sendToFather.next({formAcuerdo: status});
    });
  }

  businessRules(type: string, value: any) {
    if(type == 'tipo_despacho') {
      if(this.validarNumeroTemas()) {
        this.store.dispatch(cargaAcuerdoCampos({ oData: { id_tipo_despacho: value }}))
        this.fnConsultarTemas();
      } else {
        this.formDespacho.get('tipo_despacho')?.setValue(this.oAcuerdo.id_tipo_despacho, { emitEvent: false });
      }
    }

    if(type == 'tipo_tema') {
      if(this.validarNumeroTemas()) {
        let tema = this.catTemas.filter((item:any) => item.id_tema == value);
        if(tema.length != 0) {
          tema = tema[0];
          this.store.dispatch(cargaAcuerdoCampos({ oData: { 
            id_tema: value,
            id_empresa: this.oEntrada.empresa,
            nombre_empresa: this.oEntrada.empresa_descripcion,
            nombreTema: tema.nombreTema,
            descripcionTema: "-"
          }}));
        }
      } else {
        this.formDespacho.get('tipo_tema')?.setValue(this.oAcuerdo.id_tema, { emitEvent: false });
      }
    }
  }

  cancelarTema() {
    this.formDespacho.get('tipo_tema')?.setValue('');
    this.nuevoTema = false;
    this.formTema.reset();
  }

  guardarTema() {
    this.loadingService.show();

    const id_tipo_despacho = this.formDespacho.get('tipo_despacho')?.value;
    const nombreTema = this.formTema.get('descripcion_tema')?.value;

    let data = {
      "id_tipo_despacho": id_tipo_despacho,
      "nombreTema": nombreTema,
      "descripcionTema": "-",
      "id_empresa": this.oEntrada.empresa,
      "nombre_empresa": this.oEntrada.empresa_descripcion
    }

    this.subscriptions.add(
      this.despachoService.agregarTema(data).subscribe((result: any) => {
        if (result.error) {
          this.alertaService.open("Error 022", result.msg);
        } else {
          //GUARDAMOS LOS TEMAS CREADOS EN MEMORIA
          this.temasEnMemoria.push({
            //Valores del tema
            id_tema: result.idTema,
            id_tipo_despacho: data.id_tipo_despacho,
            nombreTema: data.nombreTema,
            id_empresa: data.id_empresa,
            nombre_empresa: data.nombre_empresa,
            
            //Valores default
            id_edo_tema: 1,
            descripcionTema: " ",
            id_fb_usr_termino: null,
            id_tema_original: null,
            justificacionTema: null
          });

          //Limpiamos Nuevo Tema
          this.formTema.reset();
          this.nuevoTema = false;

          //Cargamos Acuerdo
          this.fnConsultarTemas(result.idTema);

          //Seleccionamos Nuevo Tema
          //this.formDespacho.get('tipo_tema')?.setValue(result.idTema);
        }
        this.loadingService.hide();
      })
    );
  }

  async fnConsultarTemas (idTema: any = false) {
    const tipo_despacho = this.formDespacho.get('tipo_despacho')?.value;
    const empresa = this.oEntrada.empresa;
    const empresa_descripcion = this.oEntrada.empresa_descripcion;

    if(tipo_despacho != 0 && empresa != "") {
      this.loadingService.show();
      this.subscriptions.add(
        this.despachoService.consultarTemas(tipo_despacho, empresa, empresa_descripcion).subscribe((result: any) => {
          if(!result.error) {
            this.catTemas = result.temas;
            
            //AGREGAMOS LOS TEMAS EN MEMORIA
            const temasMemoria = this.temasEnMemoria.filter((item:any) => item.id_tipo_despacho === tipo_despacho && item.id_empresa === empresa && item.nombre_empresa === empresa_descripcion);
            this.catTemas = this.catTemas.concat(temasMemoria);
            
            //CARGAMOS EL TEMA CREADO
            if(idTema) {
              this.formDespacho.get('tipo_tema')?.setValue(idTema); //this.formDespacho.get('tipo_tema')?.setValue(idTema, { emitEvent: false});
            }

          } else {
            this.catTemas = [];
            this.alertaService.open("Error 023", result.msg);
          }
          this.loadingService.hide();
        })
      );
    } else {
      this.catTemas = [];
    }
  }

  async cargarDespacho(idTema = false) {
    this.loadingService.show();
    const tipo_despacho = this.formDespacho.get('tipo_despacho')?.value;
    const result: any = await firstValueFrom(this.despachoService.consultarTemas(tipo_despacho, this.oEntrada.empresa, this.oEntrada.empresa_descripcion));
    this.catTemas = result.temas;
    this.loadingService.hide();    
  }

  async agregarTema () {
    if(this.validarNumeroTemas())
      this.nuevoTema = true;
  }

  validarNumeroTemas() {
    if(this.oAcuerdo.numeroEntradasTema == 1)
      this.alertaService.open("Importante", "El tema debe contener al menos una entrada. Seleccione la acción ‘Quitar tema’ desde la tabla de despacho, si desea desasociar la entrada y eliminar el tema.");
    
    return (this.oAcuerdo.numeroEntradasTema == 1)?false:true;
  }

  enviaNotificacion() {
    const temaNuevo = this.temasEnMemoria.filter((item: any) => item.id_tema == this.oAcuerdo.id_tema);
    
    if(temaNuevo.length > 0)
      firstValueFrom(this.despachoService.notificar(this.fnDatosNotificar(temaNuevo[0]))).then((result: any) => { 
        debugger;
        if(result.error) {
          this.logSrvc.addLog(result); 
        } 
      });
  }

  fnDatosNotificar(oData: any) {
    return {
      "nombreUsuario":    this.oUsuario.nombre,
      "empresa":          oData.nombre_empresa,
      "tipoDespacho":     (oData.id_tipo_despacho == 1)?"Informativo":"VoBo",
      "nombreTema":       oData.nombreTema,
      "descripcionTema":  oData.descripcionTema,
      "id_empresa":       oData.id_empresa,
      "notificacion":     "nuevoTema"
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}